import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import { setLoading, setSnackInfo } from '../../../redux/actions/appAction';
import { getAllRoles, getUniqueRoleFields } from '../../../redux/actions/rolesActions';
import {
  TextField,
  Checkbox,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Slide,
  Tooltip,
  Chip,
  InputAdornment,
  CircularProgress,
  LinearProgress,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { makeStyles, withStyles } from '@material-ui/core/styles';

import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh';
import SecurityIcon from '@material-ui/icons/Security';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import SearchOffIcon from '@material-ui/icons/FindInPage';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import LockOpenIcon from '@material-ui/icons/LockOpen';

import HttpService from '../../../services/httpService';

// Shared Decorpot palette - identical constants to users, cronJobMonitor,
// ExecutionApprovalList and sendNotification, so this page recolours with them.
const ORANGE = '#F47E44';
const ORANGE_DARK = '#c1531f';
const ORANGE_LIGHT = '#fff1ea';
const HEADER_GRADIENT = `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 60%, #ff9c66 100%)`;
const INK = '#3d2b21';
const GREEN_DARK = '#19692c';
const GREEN_LIGHT = '#eaf6ee';

// The five verbs, and the colour each gets in the matrix. Reading is harmless,
// deleting is not, and the header should say so before anyone ticks a box.
const ACTIONS = [
  { key: 'READ', label: 'Read', color: '#2c5fb8', soft: '#eef3fc', hint: 'See the data' },
  { key: 'CREATE', label: 'Create', color: GREEN_DARK, soft: GREEN_LIGHT, hint: 'Add new records' },
  { key: 'UPDATE', label: 'Update', color: '#7a4b00', soft: '#fff4e2', hint: 'Change existing records' },
  { key: 'UPLOAD', label: 'Upload', color: '#7a4b9d', soft: '#f3edf9', hint: 'Attach files' },
  { key: 'DELETE', label: 'Delete', color: '#a93226', soft: '#fdecea', hint: 'Remove records permanently' },
];

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const BrandLinearProgress = withStyles(() => ({
  root: { height: 4, borderRadius: 999, backgroundColor: 'rgba(193,83,31,0.10)' },
  bar: { borderRadius: 999, background: `linear-gradient(90deg, ${ORANGE_DARK}, ${ORANGE}, #ff9c66)` },
}))(LinearProgress);

const useStyles = makeStyles((theme) => ({
  // Sized to the viewport instead of growing past it. 144px is the app chrome
  // above: antd Header 64 + Content margin 16*2 + Content padding 24*2.
  pageRoot: {
    height: 'calc(100vh - 144px)',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    // auto, NOT hidden: everything below is flex:1 + minHeight:0, so children
    // squash rather than overflow and the page can never decide it needs a
    // scrollbar. Once the chrome alone exceeds the viewport - which browser
    // zoom causes easily - `hidden` would cut the matrix off with no way back.
    overflow: 'auto',
    [theme.breakpoints.down('xs')]: { height: 'auto', minHeight: '100vh', overflow: 'visible' },
    // Zoom shrinks the CSS viewport in a way width breakpoints cannot see: a
    // zoomed 1920px monitor is still 'md' wide.
    '@media (max-height: 640px)': { height: 'auto', minHeight: 0, overflow: 'visible', paddingBottom: 32 },
  },
  banner: {
    display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0,
    padding: '14px 22px', borderRadius: 16,
    background: HEADER_GRADIENT, color: '#fff',
    boxShadow: '0 8px 24px rgba(93,55,28,0.18)', marginBottom: 12,
    [theme.breakpoints.down('xs')]: { padding: '16px', flexWrap: 'wrap' },
    '@media (max-height: 820px)': { padding: '8px 16px', marginBottom: 8, borderRadius: 12, gap: 12 },
  },
  bannerIcon: {
    display: 'grid', placeItems: 'center', width: 44, height: 44,
    borderRadius: 14, background: 'rgba(255,255,255,0.18)', flexShrink: 0,
    '@media (max-height: 820px)': { width: 34, height: 34, borderRadius: 10 },
  },
  bannerTitle: {
    fontSize: 19, fontWeight: 700, lineHeight: 1.3,
    '@media (max-height: 820px)': { fontSize: 16 },
  },
  bannerSub: {
    fontSize: 12.5, color: 'rgba(255,255,255,0.88)', marginTop: 3,
    '@media (max-height: 820px)': { display: 'none' },
  },
  bannerActions: { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  whiteBtn: {
    background: '#fff', color: ORANGE_DARK, fontWeight: 700,
    borderRadius: 10, textTransform: 'none', padding: '6px 16px',
    boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
    '&:hover': { background: ORANGE_LIGHT },
    '&.Mui-disabled': { background: 'rgba(255,255,255,0.8)', color: ORANGE_DARK, boxShadow: 'none' },
  },
  spin: { animation: '$spin 900ms linear infinite' },
  '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },

  progressSlot: { height: 4, marginBottom: 8, flexShrink: 0 },

  card: {
    flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden',
    borderRadius: 16, background: '#fff',
    border: '1px solid #eceff3', boxShadow: '0 4px 24px rgba(93,55,28,0.08)',
    padding: '14px 16px 0',
    // The floor. With minHeight:0 the matrix shrinks to zero rows and the root
    // never overflows, so its `auto` would never produce a scrollbar.
    minHeight: 320,
    [theme.breakpoints.down('xs')]: { flex: 'none', overflow: 'visible', minHeight: 0 },
    '@media (max-height: 640px)': { flex: 'none', overflow: 'visible', minHeight: 0, paddingBottom: 14 },
  },

  // ---- role picker --------------------------------------------------------
  pickerRow: {
    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    flexShrink: 0, marginBottom: 10,
  },
  roleSelect: { minWidth: 280, flex: '0 1 340px' },
  roleSummary: {
    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
    marginLeft: 'auto',
  },
  grantChip: {
    height: 26, fontSize: 11.5, fontWeight: 800, borderRadius: 999,
    background: GREEN_LIGHT, color: GREEN_DARK, border: '1px solid #bfe3c9',
  },
  totalChip: {
    height: 26, fontSize: 11.5, fontWeight: 700, borderRadius: 999,
    background: '#eef1f5', color: '#4f596a',
  },

  hintBar: {
    display: 'flex', alignItems: 'flex-start', gap: 10, flexShrink: 0,
    padding: '8px 12px', marginBottom: 10, borderRadius: 12,
    background: '#fff4e2', border: '1px solid #f3d8ab',
    fontSize: 12, color: '#7a4b00', lineHeight: 1.55,
    '@media (max-height: 820px)': { padding: '5px 10px', marginBottom: 6, fontSize: 11.5 },
  },
  hintKey: { fontWeight: 800 },
  hintClose: {
    marginLeft: 'auto', flexShrink: 0, color: '#7a4b00', padding: 4,
    '&:hover': { background: 'rgba(122,75,0,0.10)' },
  },

  toolbar: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', flexShrink: 0, marginBottom: 10 },
  searchInputRoot: { borderRadius: 24, background: '#f7f9fc' },
  resultCount: { fontSize: 12.5, color: '#5f6876', fontWeight: 600, marginLeft: 'auto' },
  resetBtn: { borderRadius: 20, textTransform: 'none', fontWeight: 600, color: ORANGE_DARK },

  // ---- matrix -------------------------------------------------------------
  tableSection: {
    flex: 1, display: 'flex', flexDirection: 'column', minHeight: 240,
    [theme.breakpoints.down('xs')]: { flex: 'none', display: 'block', minHeight: 0 },
    '@media (max-height: 640px)': { flex: 'none', minHeight: 0 },
  },
  scrollArea: {
    flex: 1, minHeight: 0, overflow: 'auto',
    borderRadius: 12, border: '1px solid #eceff3', marginBottom: 14,
    '@media (max-height: 640px)': { flex: 'none', overflow: 'visible' },
  },
  headCell: {
    backgroundColor: ORANGE_DARK, color: '#fff',
    fontWeight: 700, fontSize: 12, letterSpacing: 0.3,
    borderBottom: 'none', whiteSpace: 'nowrap', padding: '8px 10px',
    textAlign: 'center',
    // stickyHeader already sets position:sticky/top:0 - zIndex keeps the
    // background painted above the scrolling rows at the seam.
    zIndex: 3,
  },
  // The field name column stays put while the five verbs scroll sideways on a
  // narrow screen; without this you lose track of which row you are ticking.
  headCellSticky: {
    left: 0, position: 'sticky', zIndex: 4, textAlign: 'left',
  },
  headAction: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 },
  headActionDot: { width: 7, height: 7, borderRadius: '50%' },
  row: {
    transition: 'background-color 150ms ease',
    '&:nth-of-type(even)': { backgroundColor: '#fafbfd' },
    '&:hover': { backgroundColor: ORANGE_LIGHT },
    '&:hover $stickyCell': { backgroundColor: ORANGE_LIGHT },
  },
  bodyCell: { fontSize: 13, color: INK, padding: '2px 10px', textAlign: 'center' },
  stickyCell: {
    position: 'sticky', left: 0, zIndex: 2,
    backgroundColor: 'inherit', textAlign: 'left',
    padding: '2px 10px',
  },
  slCell: { fontSize: 12, color: '#636c7a', fontWeight: 700, width: 48, padding: '2px 10px' },
  fieldName: { fontSize: 12.8, fontWeight: 600, color: INK, whiteSpace: 'nowrap' },
  rowAllBtn: {
    padding: 4, borderRadius: 8,
    transition: 'transform 160ms ease',
    '&:hover': { transform: 'scale(1.12)' },
  },
  cellWrap: { display: 'grid', placeItems: 'center', minHeight: 32 },

  emptyWrap: { textAlign: 'center', padding: '48px 20px' },
  emptyTitle: { fontSize: 15, fontWeight: 700, color: '#5f6876', marginTop: 10 },
  emptyHint: { fontSize: 12.5, color: '#636c7a', marginTop: 4, maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' },

  // ---- dialog -------------------------------------------------------------
  dlgPaper: { borderRadius: 18, overflow: 'hidden' },
  dlgHead: {
    display: 'flex', alignItems: 'center', gap: 14, padding: '16px 22px',
    color: '#fff', background: 'linear-gradient(135deg, #9c3d12 0%, #a84315 45%, #c1531f 100%)',
  },
  dlgHeadIcon: {
    width: 42, height: 42, borderRadius: 13, flexShrink: 0,
    display: 'grid', placeItems: 'center', background: 'rgba(255,255,255,0.2)',
  },
  dlgTitle: { fontSize: 17, fontWeight: 700, lineHeight: 1.25 },
  dlgSub: { fontSize: 12, color: 'rgba(255,255,255,0.92)', marginTop: 2 },
  dlgClose: {
    color: '#fff', flexShrink: 0, marginLeft: 'auto',
    transition: 'background-color 200ms ease, transform 200ms ease',
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.18)', transform: 'rotate(90deg)' },
  },
  dlgBody: { padding: '18px 22px 8px' },
  dlgActions: { padding: '12px 22px 18px', gap: 8 },
  btnGhost: {
    borderRadius: 22, textTransform: 'none', fontWeight: 600, padding: '6px 18px',
    color: '#6b6460', background: '#f3f1ef', boxShadow: 'none',
    '&:hover': { background: '#eae6e3', color: '#6b6460', boxShadow: 'none' },
  },
  btnPrimary: {
    borderRadius: 22, textTransform: 'none', fontWeight: 700, padding: '6px 22px',
    background: ORANGE_DARK, color: '#fff', boxShadow: 'none',
    '&:hover': { background: '#a8450f', color: '#fff', boxShadow: 'none' },
    '&.Mui-disabled': { background: '#e6e2df', color: '#857d77' },
  },
}));

const Roles = (props) => {
  const classes = useStyles();
  const { rolesList, uniqueRoleFields } = useSelector((state) => state.roles);

  const [selectedRole, setSelectedRole] = useState('');
  const [selectedRoleData, setSelectedRoleData] = useState(null);
  const [search, setSearch] = useState('');

  const [addRoleModal, setAddRoleModal] = useState(false);
  const [roleName, setRoleName] = useState('');

  // ---- separate loaders ---------------------------------------------------
  // Previously every one of these went through the global redux spinner, so
  // ticking a single checkbox blocked the entire app behind a backdrop.
  const [listLoading, setListLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savingRole, setSavingRole] = useState(false);
  const [busyCells, setBusyCells] = useState({});   // { 'FIELD::ACTION': true }
  const [busyRow, setBusyRow] = useState('');

  const [showHint, setShowHint] = useState(() => {
    try { return localStorage.getItem('rolesPageHintDismissed') !== '1'; } catch (e) { return true; }
  });
  const dismissHint = () => {
    setShowHint(false);
    try { localStorage.setItem('rolesPageHintDismissed', '1'); } catch (e) { /* no-op */ }
  };

  // Reference data, fetched ONCE.
  //
  // This used to sit in an effect keyed on [selectedRoleData] - and
  // handleCheck replaced that object on every single checkbox - so each tick
  // fired three more full fetches, including getAllScopes for scopes the page
  // does not use. Picking a role did the same.
  const loadRoles = useCallback(async (opts = {}) => {
    if (opts.silent) setRefreshing(true); else setListLoading(true);
    try {
      await Promise.all([props.getAllRoles(), props.getUniqueRoleFields()]);
    } catch (error) {
      console.error(error);
      props.setSnackInfo(error?.response?.data?.message ?? 'Could not load roles', 'error');
    }
    setRefreshing(false);
    setListLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadRoles(); }, [loadRoles]);

  // Keep the open role in step with the list after a refetch, without making
  // the fetch depend on it.
  useEffect(() => {
    if (!selectedRole) { setSelectedRoleData(null); return; }
    const found = (rolesList || []).find((r) => r.name === selectedRole) || null;
    setSelectedRoleData(found);
  }, [selectedRole, rolesList]);

  const can = useMemo(() => new Set(selectedRoleData?.can || []), [selectedRoleData]);
  const fields = useMemo(() => (uniqueRoleFields || []).filter(Boolean), [uniqueRoleFields]);

  const shownFields = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return fields;
    return fields.filter((f) => String(f).toLowerCase().includes(term));
  }, [fields, search]);

  const grantedCount = can.size;
  const totalPossible = fields.length * ACTIONS.length;

  // ---- one permission -----------------------------------------------------
  const setPermission = async (field, action, push) => {
    const value = `${field}::${action}`;
    setBusyCells((prev) => ({ ...prev, [value]: true }));
    // Optimistic: the box moves now and is reconciled by the refetch below.
    // Waiting on a round trip before the tick appears makes the grid feel
    // broken when you are setting twenty of them.
    setSelectedRoleData((prev) => {
      if (!prev) return prev;
      const next = new Set(prev.can || []);
      if (push) next.add(value); else next.delete(value);
      return { ...prev, can: Array.from(next) };
    });
    try {
      await HttpService.editRole({ id: selectedRoleData._id, push, value });
      return true;
    } catch (error) {
      console.error(error);
      props.setSnackInfo(error?.response?.data?.message ?? 'Could not update that permission', 'error');
      // Roll the optimistic change back - leaving it would show an access
      // level the server never granted.
      setSelectedRoleData((prev) => {
        if (!prev) return prev;
        const next = new Set(prev.can || []);
        if (push) next.delete(value); else next.add(value);
        return { ...prev, can: Array.from(next) };
      });
      return false;
    } finally {
      setBusyCells((prev) => {
        const next = { ...prev };
        delete next[value];
        return next;
      });
    }
  };

  const toggleOne = async (field, action) => {
    const value = `${field}::${action}`;
    const ok = await setPermission(field, action, !can.has(value));
    if (ok) loadRoles({ silent: true });
  };

  // ---- a whole row --------------------------------------------------------
  const toggleRow = async (field) => {
    const current = ACTIONS.filter((a) => can.has(`${field}::${a.key}`));
    const push = current.length < ACTIONS.length;   // partial or empty -> grant all
    const todo = ACTIONS.filter((a) => can.has(`${field}::${a.key}`) !== push);
    if (!todo.length) return;

    setBusyRow(field);
    let failed = 0;
    // Sequential on purpose: there is no bulk endpoint, and firing five
    // concurrent writes at the same role document races them against each
    // other - the last response wins and some ticks silently revert.
    for (const action of todo) {
      // eslint-disable-next-line no-await-in-loop
      const ok = await setPermission(field, action.key, push);
      if (!ok) failed += 1;
    }
    setBusyRow('');
    if (!failed) {
      props.setSnackInfo(`${field} — ${push ? 'full access granted' : 'all access removed'}`, 'success');
    }
    loadRoles({ silent: true });
  };

  // ---- add role -----------------------------------------------------------
  const trimmedName = roleName.trim();
  const duplicateName = (rolesList || []).some((r) => String(r.name).toLowerCase() === trimmedName.toLowerCase());

  const addRole = async () => {
    if (!trimmedName || duplicateName) return;
    setSavingRole(true);
    try {
      await HttpService.addRole({ roleName: trimmedName });
      props.setSnackInfo('Role created — now give it some permissions', 'success');
      setAddRoleModal(false);
      setRoleName('');
      await loadRoles({ silent: true });
      // Drop straight into the new role, which is always the next thing you
      // want to do: an empty role grants nothing.
      setSelectedRole(trimmedName);
    } catch (error) {
      console.error(error);
      props.setSnackInfo(error?.response?.data?.message ?? 'Something went wrong', 'error');
    }
    setSavingRole(false);
  };

  const closeAddRole = () => {
    setAddRoleModal(false);
    setRoleName('');
  };

  const busy = listLoading || refreshing;

  return (
    <div className={classes.pageRoot}>
      <div className={classes.banner}>
        <div className={classes.bannerIcon}><SecurityIcon style={{ fontSize: 24 }} /></div>
        <div style={{ minWidth: 0 }}>
          <div className={classes.bannerTitle}>Roles &amp; Permissions</div>
          <div className={classes.bannerSub}>
            What each role is allowed to do, screen by screen.
          </div>
        </div>
        <div className={classes.bannerActions}>
          <Button
            className={classes.whiteBtn}
            size="small"
            disabled={busy}
            startIcon={<RefreshIcon className={refreshing ? classes.spin : undefined} />}
            onClick={() => loadRoles({ silent: true })}
          >
            {refreshing ? 'Refreshing' : 'Refresh'}
          </Button>
          <Button
            className={classes.whiteBtn}
            size="small"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setAddRoleModal(true)}
          >
            Add Role
          </Button>
        </div>
      </div>

      <div className={classes.progressSlot}>{busy ? <BrandLinearProgress /> : null}</div>

      <div className={classes.card}>
        {showHint && (
          <div className={classes.hintBar}>
            <InfoOutlinedIcon style={{ fontSize: 17, flexShrink: 0, marginTop: 1 }} />
            <span>
              <span className={classes.hintKey}>Tips:</span>{' '}
              pick a role, then tick what it may do &middot;{' '}
              changes save as you tick, there is no Save button &middot;{' '}
              the <span className={classes.hintKey}>⇶</span> button on a row grants or removes all five at once.
            </span>
            <Tooltip title="Hide these tips">
              <IconButton size="small" className={classes.hintClose} onClick={dismissHint} aria-label="hide tips">
                <ClearIcon style={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </div>
        )}

        <div className={classes.pickerRow}>
          <TextField
            select
            size="small"
            variant="outlined"
            label="Role"
            className={classes.roleSelect}
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            disabled={listLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <VpnKeyIcon style={{ fontSize: 17, color: ORANGE_DARK }} />
                </InputAdornment>
              ),
            }}
          >
            <MenuItem value=""><em>Select a role…</em></MenuItem>
            {(rolesList || []).map((role) => (
              <MenuItem value={role.name} key={role._id || role.name}>{role.name}</MenuItem>
            ))}
          </TextField>

          {selectedRoleData && (
            <div className={classes.roleSummary}>
              <Chip
                size="small"
                className={classes.grantChip}
                icon={<LockOpenIcon style={{ fontSize: 14, color: GREEN_DARK }} />}
                label={`${grantedCount} granted`}
              />
              <Chip size="small" className={classes.totalChip} label={`of ${totalPossible} possible`} />
            </div>
          )}
        </div>

        {!selectedRole ? (
          <div className={classes.emptyWrap}>
            <SecurityIcon style={{ fontSize: 50, color: '#c9d2de' }} />
            <div className={classes.emptyTitle}>Pick a role to see its permissions</div>
            <div className={classes.emptyHint}>
              {listLoading
                ? 'Loading the role list…'
                : `${(rolesList || []).length} roles are set up. Choose one above, or create a new one.`}
            </div>
          </div>
        ) : (
          <>
            <div className={classes.toolbar}>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Search screens and modules"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ minWidth: 280, flex: '0 1 360px' }}
                InputProps={{
                  classes: { root: classes.searchInputRoot },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: '#9aa5b1', fontSize: 19 }} />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch('')} aria-label="clear search">
                        <ClearIcon style={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
              {search && (
                <Button size="small" className={classes.resetBtn} startIcon={<ClearIcon style={{ fontSize: 15 }} />} onClick={() => setSearch('')}>
                  Clear
                </Button>
              )}
              <span className={classes.resultCount}>
                Showing {shownFields.length} of {fields.length}
              </span>
            </div>

            <div className={classes.tableSection}>
              <TableContainer className={classes.scrollArea}>
                <Table size="small" stickyHeader aria-label="role permissions">
                  <TableHead>
                    <TableRow>
                      <TableCell className={`${classes.headCell} ${classes.headCellSticky}`}>
                        Screen / Module
                      </TableCell>
                      {ACTIONS.map((a) => (
                        <TableCell key={a.key} className={classes.headCell}>
                          <Tooltip title={a.hint}>
                            <span className={classes.headAction}>
                              <span className={classes.headActionDot} style={{ background: a.soft }} />
                              {a.label}
                            </span>
                          </Tooltip>
                        </TableCell>
                      ))}
                      <TableCell className={classes.headCell}>All</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {listLoading ? (
                      [...Array(10)].map((_, i) => (
                        <TableRow key={`sk-${i}`}>
                          {[...Array(ACTIONS.length + 2)].map((__, j) => (
                            <TableCell key={j} className={classes.bodyCell}><Skeleton height={22} /></TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : shownFields.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={ACTIONS.length + 2} style={{ borderBottom: 'none' }}>
                          <div className={classes.emptyWrap}>
                            <SearchOffIcon style={{ fontSize: 46, color: '#c9d2de' }} />
                            <div className={classes.emptyTitle}>Nothing matches “{search.trim()}”</div>
                            <div className={classes.emptyHint}>{fields.length} screens are available in total.</div>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      shownFields.map((field, i) => {
                        const rowBusy = busyRow === field;
                        const onCount = ACTIONS.filter((a) => can.has(`${field}::${a.key}`)).length;
                        const allOn = onCount === ACTIONS.length;
                        return (
                          <TableRow key={field} className={classes.row}>
                            <TableCell className={`${classes.bodyCell} ${classes.stickyCell}`}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span className={classes.slCell}>{i + 1}</span>
                                <span className={classes.fieldName}>{field}</span>
                              </span>
                            </TableCell>
                            {ACTIONS.map((a) => {
                              const value = `${field}::${a.key}`;
                              const cellBusy = Boolean(busyCells[value]);
                              return (
                                <TableCell key={a.key} className={classes.bodyCell}>
                                  <span className={classes.cellWrap}>
                                    {/* One spinner per cell, not a page-wide
                                        block - ticking Read on one screen
                                        should not freeze the other 200 boxes. */}
                                    {cellBusy ? (
                                      <CircularProgress size={16} thickness={5} style={{ color: a.color }} />
                                    ) : (
                                      <Checkbox
                                        size="small"
                                        checked={can.has(value)}
                                        disabled={rowBusy}
                                        onChange={() => toggleOne(field, a.key)}
                                        style={{ color: can.has(value) ? a.color : '#b9bfc9', padding: 5 }}
                                        inputProps={{ 'aria-label': `${a.label} ${field}` }}
                                      />
                                    )}
                                  </span>
                                </TableCell>
                              );
                            })}
                            <TableCell className={classes.bodyCell}>
                              <Tooltip title={allOn ? `Remove all access to ${field}` : `Give full access to ${field}`}>
                                <span>
                                  <IconButton
                                    size="small"
                                    className={classes.rowAllBtn}
                                    disabled={rowBusy}
                                    onClick={() => toggleRow(field)}
                                    aria-label={allOn ? 'remove all' : 'grant all'}
                                  >
                                    {rowBusy
                                      ? <CircularProgress size={16} thickness={5} style={{ color: ORANGE_DARK }} />
                                      : allOn
                                        ? <RemoveCircleOutlineIcon style={{ fontSize: 19, color: '#a93226' }} />
                                        : <DoneAllIcon style={{ fontSize: 19, color: onCount ? ORANGE_DARK : '#9aa5b1' }} />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </>
        )}
      </div>

      {/* ------------------------------ add role ----------------------------- */}
      <Dialog
        open={addRoleModal}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="xs"
        onClose={closeAddRole}
        classes={{ paper: classes.dlgPaper }}
      >
        <div className={classes.dlgHead}>
          <div className={classes.dlgHeadIcon}><AddCircleOutlineIcon /></div>
          <div style={{ minWidth: 0 }}>
            <div className={classes.dlgTitle}>Add Role</div>
            <div className={classes.dlgSub}>It starts with no permissions at all</div>
          </div>
          <IconButton size="small" className={classes.dlgClose} onClick={closeAddRole} aria-label="close">
            <ClearIcon fontSize="small" />
          </IconButton>
        </div>
        <DialogContent className={classes.dlgBody}>
          <TextField
            label="Role name"
            fullWidth
            size="small"
            variant="outlined"
            value={roleName}
            autoFocus
            onChange={(e) => setRoleName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && trimmedName && !duplicateName && !savingRole) addRole(); }}
            error={duplicateName}
            helperText={duplicateName ? 'A role with that name already exists' : ' '}
          />
          <DialogContentText style={{ fontSize: 12.5, color: '#636c7a', marginBottom: 0 }}>
            You will be dropped straight into its permission grid once it is created.
          </DialogContentText>
        </DialogContent>
        <DialogActions className={classes.dlgActions}>
          <Button className={classes.btnGhost} onClick={closeAddRole} disabled={savingRole}>Cancel</Button>
          <Button
            className={classes.btnPrimary}
            onClick={addRole}
            disabled={!trimmedName || duplicateName || savingRole}
            startIcon={savingRole
              ? <CircularProgress size={15} thickness={5} style={{ color: '#fff' }} />
              : <AddCircleOutlineIcon style={{ fontSize: 16 }} />}
          >
            {savingRole ? 'Creating…' : 'Create Role'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default connect(null, { setLoading, setSnackInfo, getUniqueRoleFields, getAllRoles })(Roles);
